import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  configuracaoToSalvarRequerimentoCommand,
  requerimentoDtoToSalvo,
} from '../../mappers/parametros/requerimento-online-api.mapper';
import {
  documentoTipoDtoToOpcao,
  provedorAssinaturaDtoToProvedor,
} from '../../mappers/parametros/resgate-configuracao-api.mapper';
import { ProvedorAssinatura, ResgateDocumentoOpcao } from '../../models/parametros/resgate-configuracao.model';
import {
  RequerimentoOnlineConfiguracao,
  RequerimentoOnlineSalvo,
} from '../../models/parametros/requerimento-online.model';
import { InstitutosParamDocsApiService } from './institutos-param-docs-api.service';
import { RequerimentoOnlineApiService } from './requerimento-online-api.service';
import { ResgateConfiguracaoApiService } from './resgate-configuracao-api.service';

/** Erro ao carregar um plano sem nenhum tipo de requerimento ativo no catálogo. */
export class TipoRequerimentoNaoCadastradoError extends Error {
  constructor() {
    super('Nenhum tipo de requerimento ativo está cadastrado.');
    this.name = 'TipoRequerimentoNaoCadastradoError';
  }
}

/**
 * Configuração do requerimento online de cada plano (`/v1/planos-param/{id}/requerimento-online`).
 * A API guarda uma configuração por tipo de requerimento; a tela edita a do primeiro tipo ativo
 * (hoje só existe Benefício). Provedores e documentos vêm dos mesmos catálogos do resgate.
 */
@Injectable({
  providedIn: 'root',
})
export class RequerimentoOnlineService {
  private readonly apiService = inject(RequerimentoOnlineApiService);
  private readonly resgateApiService = inject(ResgateConfiguracaoApiService);
  private readonly docsApiService = inject(InstitutosParamDocsApiService);

  obterConfiguracao(planoId: number): Observable<RequerimentoOnlineSalvo> {
    return this.apiService.listarConfiguracoes(planoId).pipe(
      map(([primeira]) => {
        if (!primeira) {
          throw new TipoRequerimentoNaoCadastradoError();
        }
        return requerimentoDtoToSalvo(primeira);
      }),
    );
  }

  /** Grava a configuração inteira do tipo e devolve o que a API gravou. */
  salvarConfiguracao(
    planoId: number,
    idTipoRequerimento: number,
    configuracao: RequerimentoOnlineConfiguracao,
  ): Observable<RequerimentoOnlineSalvo> {
    return this.apiService
      .salvarConfiguracao(planoId, idTipoRequerimento, configuracaoToSalvarRequerimentoCommand(configuracao))
      .pipe(map(requerimentoDtoToSalvo));
  }

  /** Provedores de assinatura ativos, para o campo "Provedor". */
  listarProvedores(): Observable<ProvedorAssinatura[]> {
    return this.resgateApiService
      .listarProvedoresAssinatura({ ativo: 'S' })
      .pipe(map((provedores) => provedores.map(provedorAssinaturaDtoToProvedor)));
  }

  /** Tipos de documento ativos, que podem ser incluídos na configuração. */
  listarDocumentosDisponiveis(): Observable<ResgateDocumentoOpcao[]> {
    return this.docsApiService
      .listarTiposDocumento({ ativo: 'S' })
      .pipe(map((tipos) => tipos.map(documentoTipoDtoToOpcao)));
  }
}
